import { useCallback, useMemo } from 'react';
import { useWebStorageState, type StorageKind } from '@reflex/ui/hooks/useWebStorageState';

/**
 * Filters `items` by `status` using a STATUS_GROUP lookup map.
 * If `status` is null or not found in the map, all items are returned.
 */
export function filterByStatusGroup<S, T>(
  items: T[],
  status: string | null,
  statusGroup: Record<string, S[]>,
  getStatus: (item: T) => S,
): T[] {
  if (!status) return items;
  const allowed = statusGroup[status];
  if (!allowed) return items;
  return items.filter((item) => allowed.includes(getStatus(item)));
}

// --- Generic entity filter hook ---

export interface EntityFilterConfig<TStored extends object, TFilters, TItem, TStatus> {
  storageKey: string;
  /**
   * Which storage area persists the filters. Defaults to `'local'` (shared
   * across tabs). Pass `'session'` to keep filters private to each tab.
   */
  storageKind?: StorageKind;
  defaults: TStored;
  normalize: (stored: TStored, defaults: TStored) => TFilters;
  statusGroupMap: Record<string, TStatus[]>;
  getStatusFilter: (filters: TFilters) => string | null;
  getItemStatus: (item: TItem) => TStatus;
  extraFilter?: (items: TItem[], filters: TFilters) => TItem[];
}

export interface EntityFilterResult<TStored extends object, TFilters, TItem> {
  filters: TFilters;
  setField: (key: string, value: unknown) => void;
  /** Persist several fields in a single write (e.g. a "clear all" reset). */
  setFields: (values: Partial<TStored>) => void;
  filterItems: (items: TItem[]) => TItem[];
}

// Stable reference so every consumer of `useEntityFilters` shares the same
// "nothing stored yet" sentinel passed to `useLocalStorageState`.
const EMPTY_STORED = Object.freeze({}) as Readonly<Record<string, never>>;

/**
 * Generic Web Storage-persisted entity filter hook.
 *
 * Handles storage, normalization, status-group filtering, and optional
 * extra filtering. Consumers wrap this with entity-specific named setters
 * and re-export the public API unchanged. `storageKind` selects the storage
 * area (`'local'` by default, shared across tabs; `'session'` to keep the
 * filters private to each tab).
 *
 * Note: the stored default is always `{}`, not `defaults`. The `defaults`
 * object is only consulted by `normalize()` for keys missing from the stored
 * partial. This matters because the storage store is cached globally by key on
 * first render, so if we seeded the default with values derived from props that
 * can change after mount (e.g. the current user id), the cached snapshot would
 * pin them to their initial (often stale) values. By keeping stored as a
 * partial, `normalize` can always pick up the latest `defaults` for any key the
 * user has not explicitly persisted.
 */
export function useEntityFilters<TStored extends object, TFilters, TItem, TStatus>({
  storageKey,
  storageKind = 'local',
  defaults,
  normalize,
  statusGroupMap,
  getStatusFilter,
  getItemStatus,
  extraFilter,
}: EntityFilterConfig<TStored, TFilters, TItem, TStatus>): EntityFilterResult<
  TStored,
  TFilters,
  TItem
> {
  const [stored, setStored] = useWebStorageState<Partial<TStored>>(
    storageKind,
    storageKey,
    EMPTY_STORED as Partial<TStored>,
  );

  const filters = useMemo(
    () => normalize(stored as TStored, defaults),
    [stored, defaults, normalize],
  );

  const setField = useCallback(
    (key: string, value: unknown) => {
      setStored((prev) => ({ ...prev, [key]: value }) as Partial<TStored>);
    },
    [setStored],
  );

  // One write for a whole group of fields. A "clear all" that looped over
  // `setField` would persist each key in its own storage write, and every
  // subscriber would re-render once per field.
  const setFields = useCallback(
    (values: Partial<TStored>) => {
      setStored((prev) => ({ ...prev, ...values }));
    },
    [setStored],
  );

  const filterItems = useCallback(
    (items: TItem[]): TItem[] => {
      const statusValue = getStatusFilter(filters);
      let result = filterByStatusGroup(items, statusValue, statusGroupMap, getItemStatus);
      if (extraFilter) {
        result = extraFilter(result, filters);
      }
      return result;
    },
    [filters, getStatusFilter, statusGroupMap, getItemStatus, extraFilter],
  );

  return { filters, setField, setFields, filterItems };
}
